import React from "react";

// Reusable brand logo that supports:
// - Inline SVG parts (icon/wordmark) that inherit color via currentColor
// - External SVGs via CSS mask (asMask) so they can be tinted with color
// - Fallback <img> for src when not using mask
// - One clickable link wrapping the entire logo
//
// Props:
// - href, alt: link target and accessible label
// - icon: { svg?: ReactNode, src?: string, asMask?: boolean, width?, height? }
// - wordmark: same shape as icon
// - gap: CSS size between icon and wordmark (default var(--paddingSM))
// - partClass: class applied to each visual part (e.g., 'rm-footer__logo' or 'rm-navbar__logo')
// - className: optional container class for additional styling
export default function Logo({
  href,
  alt = "Logo",
  // Simple API: pass icon and wordmark (or an array of items)
  // Each part: { svg?, src?, asMask?, width?, height?, className?, style? }
  icon,
  wordmark,
  items,
  // Visibility controls
  showIcon = true,
  showWordmark = true,
  // Extra CSS classes for each part
  iconClass,
  wordmarkClass,
  // Layout + styling
  gap = "var(--paddingSM)",
  partClass = "rm-logo",
  className = "",
}) {
  // Default placeholders if nothing passed
  const defaultIcon = { src: "/assets/img/logo_icon.svg", asMask: true, width: 28, height: 28 };
  const defaultWordmark = { src: "/assets/img/logo_type.svg", asMask: true, height: 18 };
  const usingItems = Array.isArray(items) && items.length > 0;
  // Build parts list (used when items[] provided)
  const partsList = usingItems
    ? items.filter(Boolean)
    : [showIcon ? icon : null, showWordmark ? wordmark : null].filter(Boolean);
  const useFallback = partsList.length === 0;

  const Part = ({ part, kind, extraClass }) => {
    if (!part) return null;
    const { svg, src, asMask, width, height, className: cls, style } = part;
    const roleKind = kind || part.kind; // allow part to carry its own kind
    const maskStyle = asMask && src ? { "--logo-url": `url(${src})` } : style;

    // For accessibility, inner pieces are decorative; label the wrapper instead
    const commonProps = {
      "aria-hidden": true,
      className: [
        partClass,
        roleKind === "icon"
          ? "rm-logo__icon"
          : roleKind === "wordmark"
          ? "rm-logo__wordmark"
          : "",
        asMask ? "rm-logo--mask" : "",
        extraClass,
        cls,
      ]
        .filter(Boolean)
        .join(" "),
      style: maskStyle,
    };

    if (asMask && src) {
      return <span {...commonProps} />;
    }
    if (svg) {
      return <span {...commonProps}>{svg}</span>;
    }
    if (src) {
      return (
        <span {...commonProps}>
          <img
            src={src}
            alt=""
            width={width || 32}
            height={height || 32}
            loading="lazy"
            decoding="async"
          />
        </span>
      );
    }
    return null;
  };

  const content = (
    <span
      className={["rm-logo grid", className].filter(Boolean).join(" ")}
    >
      {usingItems
        ? partsList.map((p, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Part key={idx} part={p} kind={p?.kind} />
          ))
        : useFallback
        ? [
            <Part key="icon" part={defaultIcon} kind="icon" extraClass={iconClass} />,
            <Part key="wordmark" part={defaultWordmark} kind="wordmark" extraClass={wordmarkClass} />,
          ]
        : [
            showIcon && icon ? (
              <Part key="icon" part={icon} kind="icon" extraClass={iconClass} />
            ) : null,
            showWordmark && wordmark ? (
              <Part key="wordmark" part={wordmark} kind="wordmark" extraClass={wordmarkClass} />
            ) : null,
          ].filter(Boolean)}
    </span>
  );

  if (href) {
    return (
      <a href={href} aria-label={alt} className="rm-logo-link">
        {content}
      </a>
    );
  }
  // Non-link: ensure accessible name on the visual wrapper
  return React.cloneElement(content, { role: "img", "aria-label": alt });
}
